import React, { useCallback } from 'react';
import { useFontContext } from '../context/FontContext.jsx';

/**
 * Sticky top bar. Holds the three global preview controls: the sample
 * text, the shared specimen size, and light/dark theme. Deliberately
 * plain — the typography being previewed below is the star, so this bar
 * stays quiet and functional.
 */
export default function ControlPanel({ fontCount }) {
  const {
    customText,
    setCustomText,
    fontSize,
    setFontSize,
    theme,
    toggleTheme,
    searchTerm,
    setSearchTerm
  } = useFontContext();


  const handleClear = useCallback(() => setCustomText(''), [setCustomText]);

  return (
    <header
      className="sticky top-0 z-20 border-b border-ink/10 bg-paper/95 backdrop-blur
                 dark:border-paper/10 dark:bg-night/95"
    >
      <div className="mx-auto flex max-w-6xl flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:gap-6">
        {/* Brand mark */}
        <div className="flex shrink-0 items-baseline gap-2">
          <span className="font-display text-xl font-medium tracking-tight">
            Образец
          </span>
          <span className="font-mono text-xs text-ink/50 dark:text-paper/50">
            {fontCount} {
			fontCount % 10 === 1 && fontCount % 100 !== 11 
			? 'шрифт' 
			: [2, 3, 4].includes(fontCount % 10) && ![12, 13, 14].includes(fontCount % 100) 
			? 'шрифта' 
			: 'шрифтов'
			}

          </span>
        </div>
		
        {/* Поиск по названию */}
        <div className="relative flex-1 max-w-xs">
          <input
            type="text"
            placeholder="Поиск шрифта..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full rounded-none border border-ink/20 bg-transparent px-3 py-1.5 font-sans text-sm placeholder:text-ink/40 focus:border-forest dark:border-paper/20 dark:placeholder:text-paper/40 dark:focus:border-amber"
          />
          {searchTerm && (
            <button
              type="button"
              onClick={() => setSearchTerm('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-ink/40 hover:text-ink dark:text-paper/40 dark:hover:text-paper text-xs"
            >
              ✕
            </button>
          )}
        </div>
		

        {/* Sample text input */}
        <div className="relative flex-1">
          <label htmlFor="specimen-text" className="sr-only">
            Preview text
          </label>
          <input
            id="specimen-text"
            type="text"
            value={customText}
            onChange={(e) => setCustomText(e.target.value)}
            placeholder="Type something to preview…"
            className="w-full rounded-none border-b border-ink/20 bg-transparent px-1 py-1.5
                       font-sans text-sm placeholder:text-ink/40 focus:border-forest
                       dark:border-paper/20 dark:placeholder:text-paper/40 dark:focus:border-amber"
          />
          {customText && (
            <button
              type="button"
              onClick={handleClear}
              aria-label="Clear preview text"
              className="absolute right-1 top-1/2 -translate-y-1/2 text-ink/40 hover:text-ink
                         dark:text-paper/40 dark:hover:text-paper"
            >
              ×
            </button>
          )}
        </div>

        {/* Size slider */}
        <div className="flex shrink-0 items-center gap-3">
          <label htmlFor="specimen-size" className="font-mono text-xs text-ink/60 dark:text-paper/60">
            {fontSize}px
          </label>
          <input
            id="specimen-size"
            type="range"
            min={8}
            max={120}
            value={fontSize}
            onChange={(e) => setFontSize(Number(e.target.value))}
            className="h-1 w-32 cursor-pointer appearance-none rounded-full bg-ink/15 accent-forest
                       dark:bg-paper/15 dark:accent-amber"
          />
        </div>

        {/* Theme toggle */}
        <button
          type="button"
          onClick={toggleTheme}
          aria-label={`Переключить на ${theme === 'light' ? 'тёмную' : 'светлую'} тему`}
          aria-pressed={theme === 'dark'}
          className="shrink-0 rounded-full border border-ink/20 px-3 py-1.5 font-mono text-xs
                     hover:border-forest hover:text-forest dark:border-paper/20 dark:hover:border-amber
                     dark:hover:text-amber"
        >
          {theme === 'light' ? 'Тёмная' : 'Светлая'}
        </button>
      </div>
    </header>
  );
}
